import { Button } from '@/client/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/client/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';

interface Member {
  id: string;
  full_name: string;
  job_title: string;
  email: string;
}

interface AvailableEmployee {
  id: string;
  full_name: string | null;
  email: string;
  job_title: string | null;
}

interface MembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  members: Member[];
  availableEmployees: AvailableEmployee[];
  isLoading: boolean;
  onAddMember: (employeeId: string) => void;
  onRemoveMember: (employeeId: string) => void;
  isAddPending: boolean;
  isRemovePending: boolean;
}

export function MembersDialog({
  open,
  onOpenChange,
  members,
  availableEmployees,
  isLoading,
  onAddMember,
  onRemoveMember,
  isAddPending,
  isRemovePending,
}: MembersDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage Members</DialogTitle>
        </DialogHeader>
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!isLoading && (
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-sm font-medium">Current Members</p>
              {members.length === 0 && (
                <p className="text-sm text-muted-foreground">No members yet.</p>
              )}
              <ul className="divide-y rounded-md border">
                {members.map((m) => (
                  <li key={m.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div>
                      <span className="font-medium">{m.full_name || m.email}</span>
                      {m.job_title && (
                        <span className="ml-2 text-muted-foreground">{m.job_title}</span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      disabled={isRemovePending}
                      onClick={() => onRemoveMember(m.id)}
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
            {availableEmployees.length > 0 && (
              <div>
                <p className="mb-2 text-sm font-medium">Add Member</p>
                <Select onValueChange={onAddMember} disabled={isAddPending}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee…" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableEmployees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.full_name || e.email}
                        {e.job_title ? ` — ${e.job_title}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
